/**
 * Three-stage journey strip (s80 dashboard redesign): About you → The ten
 * essentials → Complete the picture. Soft guidance, never a gate — all three are
 * clickable at all times; exactly one is the "active" stage (2px sage border, the
 * strip's only emphasis), OR NONE when the plan is complete (s176 ruling — "active"
 * means where you are working, which is nowhere on a finished plan). State derives
 * from `useDashboardProgress`:
 *  - Stage 1 done  = About You section complete. (The "≥5/7" this comment used to claim was
 *    RETIRED at s110, D-110.3 — the section completes only when every required field is
 *    filled-or-declined. s187: Stage 1 also carries a counter, read from the shared math.)
 *  - Stage 2 active = Stage 1 done && essentials not yet all in place.
 *  - Stage 3 active = all applicable essentials in place && plan not yet complete.
 *  - Stage 3 done  = `planComplete` (s176) — the SAME value behind NextUpCard's
 *    "Your plan is complete." banner, so the check and the banner cannot disagree.
 *    Before s176 this was hardcoded `false`, so a finished plan showed two checks
 *    and one stage that could never complete (COMPLETE-STATE-1).
 */
import { useLocation } from "wouter";
import { Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { Client } from "@shared/schema";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { useDashboardProgress } from "@/hooks/useDashboardProgress";
import { FONT, SAGE, WARM } from "@shared/brand";


function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function StageCard({
  index,
  title,
  detail,
  done,
  active,
  onClick,
}: {
  index: number;
  title: string;
  detail: string;
  done: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`stage-card-${index}`}
      data-active={active ? "true" : "false"}
      data-done={done ? "true" : "false"}
      className="flex-1 text-left rounded-xl p-4 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-600/40"
      style={{
        backgroundColor: done ? SAGE[50] : WARM.white,
        border: `${active ? 2 : 1}px solid ${active ? SAGE[600] : WARM.stone}`,
      }}
    >
      {/* 1D: the "Stage N" label always shows (incl. when done) so every card —
          About You included — carries its stage marker; a done card adds the check. */}
      <div className="flex items-center gap-2 mb-1.5">
        {done && (
          <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: SAGE[600] }}>
            <Check className="w-3 h-3 text-white" />
          </span>
        )}
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
          style={done || active ? { color: SAGE[600], backgroundColor: SAGE[50] } : { color: WARM.taupe }}
        >
          Stage {index}
        </span>
      </div>
      <p className="text-sm font-semibold leading-tight" style={{ fontFamily: FONT.serif, color: WARM.ink }}>
        {title}
      </p>
      <p className="text-xs mt-1" style={{ color: WARM.taupe }}>
        {detail}
      </p>
    </button>
  );
}

export function JourneyStageStrip({ clientData }: { clientData: Client }) {
  const [, setLocation] = useLocation();
  const { isLoading, score, total, identityDone, identityFilled, identityTotal, essentialsComplete, planComplete, sectionsComplete, sectionsTotal } =
    useDashboardProgress(clientData);

  if (isLoading) return null;

  const stage1Done = identityDone;
  const stage2Done = essentialsComplete;
  const stage3Done = planComplete;
  // Exactly one active stage — the first not-yet-done step — or NONE (0) once the
  // plan is complete, so a finished strip reads as three done stages rather than
  // one that is simultaneously done and "where you are working" (s176 ruling).
  const activeStage = planComplete ? 0 : !stage1Done ? 1 : !essentialsComplete ? 2 : 3;

  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="journey-stage-strip">
      <StageCard
        index={1}
        title="About you"
        detail={
          // s187 (A4 Build 2a): Stage 1 was the only card with no counter. Same three-way
          // shape as Stage 3 below — done → prose, measurable → "N of M", unmeasurable →
          // the original prose. `identityTotal === 0` means the shared math returned null
          // (section absent, hidden, or no required field); "0 of 0" would be a claim, the
          // prose is not.
          //
          // 🔴 The numbers come from `sectionProgress` at runtime and are NEVER hardcoded.
          // Three documented figures disagree about About You's denominator — a stale "≥5/7"
          // comment (retired s110, D-110.3), CLAUDE.md's "9/9", and a raw field walk giving
          // 25 of 34. Rendering the shared math's answer is what makes all three irrelevant.
          stage1Done
            ? "Your identity details are in place."
            : identityTotal > 0
              ? `${identityFilled} of ${identityTotal} details`
              : "The basics anyone settling your estate will need."
        }
        done={stage1Done}
        active={activeStage === 1}
        onClick={() => setLocation(`/category/${CATEGORY_SLUGS.ABOUT_YOU}`)}
      />
      <StageCard
        index={2}
        title="The 10 essentials"
        detail={`${score} of ${total} in place`}
        done={stage2Done}
        active={activeStage === 2}
        onClick={() => scrollTo("start-here")}
      />
      <StageCard
        index={3}
        title="Complete the picture"
        detail={
          stage3Done
            ? "Every section is filled in."
            : sectionsTotal > 0
              ? `${sectionsComplete} of ${sectionsTotal} sections`
              : "Everything beyond the essentials."
        }
        done={stage3Done}
        active={activeStage === 3}
        onClick={() => scrollTo("next-up")}
      />
    </div>
  );
}
